import { useCallback, useEffect, useRef, useState } from 'react';

import {
  backendStatusSchema,
  type BackendStatus,
  type MapConfiguration,
  type MapFeaturesResponse,
  type WorldAnalysisResponse,
} from '@satismap/shared';

import {
  getBackendStatus,
  getEventStreamUrl,
  getMapConfiguration,
  getMapFeatures,
  getWorldAnalysis,
} from '../api/client.js';

export interface SatisMapDataState {
  status: BackendStatus | null;
  mapData: MapFeaturesResponse | null;
  mapConfiguration: MapConfiguration | null;
  analysisData: WorldAnalysisResponse | null;
  error: string | null;
  loading: boolean;
  refreshing: boolean;
  reload: () => Promise<void>;
}

function friendlyError(error: unknown, hasCachedData: boolean): string {
  const detail = error instanceof Error ? error.message : 'Unbekannter Fehler';
  return hasCachedData
    ? `Das Backend ist momentan nicht erreichbar. Der letzte geladene Stand bleibt sichtbar. (${detail})`
    : `Das Backend ist momentan nicht erreichbar. (${detail})`;
}

export function useSatisMapData(): SatisMapDataState {
  const [status, setStatus] = useState<BackendStatus | null>(null);
  const [mapData, setMapData] = useState<MapFeaturesResponse | null>(null);
  const [mapConfiguration, setMapConfiguration] =
    useState<MapConfiguration | null>(null);
  const [analysisData, setAnalysisData] =
    useState<WorldAnalysisResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const mounted = useRef(true);
  const hasLoadedOnce = useRef(false);
  const cachedData = useRef<MapFeaturesResponse | null>(null);

  useEffect(() => {
    cachedData.current = mapData;
  }, [mapData]);

  const reload = useCallback(async (): Promise<void> => {
    if (hasLoadedOnce.current) {
      setRefreshing(true);
    }
    const results = await Promise.allSettled([
      getBackendStatus(),
      getMapFeatures(),
      getWorldAnalysis(),
      mapConfiguration ? Promise.resolve(mapConfiguration) : getMapConfiguration(),
    ]);
    if (!mounted.current) {
      return;
    }

    const [statusResult, featuresResult, analysisResult, mapResult] = results;
    if (statusResult.status === 'fulfilled') {
      setStatus(statusResult.value);
    }
    if (featuresResult.status === 'fulfilled') {
      setMapData(featuresResult.value);
    }
    if (analysisResult.status === 'fulfilled') {
      setAnalysisData(analysisResult.value);
    }
    if (mapResult.status === 'fulfilled') {
      setMapConfiguration(mapResult.value);
    }

    const firstFailure = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    );
    setError(
      firstFailure
        ? friendlyError(firstFailure.reason, cachedData.current !== null)
        : null,
    );
    hasLoadedOnce.current = true;
    setLoading(false);
    setRefreshing(false);
  }, [mapConfiguration]);

  useEffect(() => {
    mounted.current = true;
    const initialLoadTimer = window.setTimeout(() => void reload(), 0);

    const pollTimer = window.setInterval(() => void reload(), 30_000);
    const eventSource = new EventSource(getEventStreamUrl(), {
      withCredentials: true,
    });
    eventSource.addEventListener('status', (event) => {
      if (!(event instanceof MessageEvent)) {
        return;
      }
      try {
        const parsed = backendStatusSchema.safeParse(
          JSON.parse(String(event.data)) as unknown,
        );
        if (parsed.success && mounted.current) {
          setStatus(parsed.data);
        }
      } catch {
        // Polling remains the fallback for malformed or interrupted SSE data.
      }
    });
    const reloadAfterImport = (): void => void reload();
    eventSource.addEventListener('save-updated', reloadAfterImport);
    eventSource.addEventListener('parse-completed', reloadAfterImport);

    return () => {
      mounted.current = false;
      window.clearTimeout(initialLoadTimer);
      window.clearInterval(pollTimer);
      eventSource.close();
    };
  }, [reload]);

  return {
    status,
    mapData,
    mapConfiguration,
    analysisData,
    error,
    loading,
    refreshing,
    reload,
  };
}
